import * as React from 'react'
import useContractFieldSetOptions from '../options/useContractFieldSetOptions'
import { useContractForm } from '@/modules/drivers/context/ContractFormContext'
import StageHeader from '../../shared/StageHeader'
import FormLayout from '../../shared/FormLayout'
import { DateField } from '@/ui/form/controls/DateField'
import { FieldsetSelect } from '@/modules/settings/components/FieldsetSelect'
import FullTimeField from '../inputs/FullTimeField'
import CheckboxWithLabelField from '../inputs/CheckboxWithLabelField'
import { Divider } from '../../shared/Divider'
import { ContractType } from '@/modules/drivers/types/contracts'
import { SettlementType } from '@/app/constants/settlementType'
import RateOptions from '../components/shared/RateOptions'
import LabelHeader from '../components/LabelHeader'
import { faCalendarRegular, FaIcon } from '@/lib/fa'
import useFormatDate from '@/modules/drivers/hooks/useFormatDate'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

const ContractParameters = ({ companyId }) => {
  const { t } = useLocalize()
  const { contractTypeOptions, contractSettlementOptions } =
    useContractFieldSetOptions()
  const { formatDate } = useFormatDate()
  const { setData, data, isAnnexState, lastAnnexDateState } = useContractForm()
  // Parytet z WPF (RefreshSettlementTypes): "wg umowy miesięcznie" tylko dla umowy cywilnoprawnej.
  const isCivilLawAgreement = data?.type === ContractType.CivilLawAgreement
  const settlementOptions = isCivilLawAgreement
    ? contractSettlementOptions
    : contractSettlementOptions.filter(
        (o) => o.value !== SettlementType.ForContract,
      )
  return (
    <div className="tw-flex tw-justify-between">
      <StageHeader title={t('contract.contract-parameters')} />
      <FormLayout>
        <DateField
          required
          label={t('contract.date-from')}
          value={data?.startDate}
          setValue={(e) => setData({ ...data, startDate: e })}
          helper={t('contract.dates-cannot-overlap')}
        />
        {!isAnnexState && (
          <DateField
            label={t('contract.date-to')}
            helper={t('contract.dates-cannot-overlap')}
            value={data?.endDate}
            setValue={(e) => setData({ ...data, endDate: e })}
          />
        )}
        {isAnnexState && (
          <div className="tw-flex tw-flex-col tw-gap-3 tw-rounded tw-bg-[#F2F2F2]/50 tw-p-3 tw-text-sm">
            <p>{t('contract.last-annex-date')}</p>
            <div className="tw-flex tw-items-center tw-gap-2">
              <FaIcon icon={faCalendarRegular} />
              <p>{formatDate(lastAnnexDateState)}</p>
            </div>
          </div>
        )}
        <Divider />
        <div>
          <FieldsetSelect
            required
            label={t('contract.employment-system')}
            placeholder={t('common.select')}
            value={data?.type ?? ''}
            name="type"
            onChange={(e) => {
              const type = e.target.value as ContractType
              setData({
                ...data,
                type,
                settlement:
                  data?.settlement === SettlementType.ForContract &&
                  type !== ContractType.CivilLawAgreement
                    ? SettlementType.Month
                    : data?.settlement,
              })
            }}
            options={contractTypeOptions}
          />
          {(data?.type === 'CivilLawAgreement' ||
            data?.type === 'SelfEmployment') && (
            <>
              <CheckboxWithLabelField
                field="calculateDutyInCivilLawAgreement"
                data={data}
                setData={setData}
                label={t('contract.calculate-duty-contributions')}
              />
              <CheckboxWithLabelField
                field="mapActivityForCivilAgreement"
                data={data}
                setData={setData}
                label={t('contract.include-event-changes')}
              />
            </>
          )}

          {data?.type &&
            data?.type !== 'NotSet' &&
            data?.type !== 'CivilLawAgreement' &&
            data?.type !== 'SelfEmployment' && (
              <>
                <CheckboxWithLabelField
                  field="personalScheduleMode"
                  data={data}
                  setData={setData}
                  label={t('contract.individual-work-schedule')}
                />
              </>
            )}
        </div>
        <div>
          <FieldsetSelect
            required
            label={t('contract.settlement')}
            placeholder={t('common.select')}
            value={data?.settlement ?? ''}
            name="type"
            onChange={(e) => {
              setData({
                ...data,
                settlement: e.target.value as SettlementType,
              })
            }}
            options={settlementOptions}
          />
          {data?.settlement && data.settlement !== 'NotSet' && (
            <RateOptions setData={setData} data={data} companyId={companyId} />
          )}
        </div>
        {data?.type !== 'CivilLawAgreement' && (
          <FullTimeField
            data={data}
            setData={setData}
            label={t('contract.full-time-dimension')}
            labelSecondary={t('contract.weekly-work-hours')}
          />
        )}
        <Divider />
        <LabelHeader label={t('contract.business-trip-options')} />
        <div>
          <CheckboxWithLabelField
            field="skipAccommodationGeneration"
            data={data}
            setData={setData}
            label={t('contract.skip-lump-costs')}
          />
          <CheckboxWithLabelField
            field="alimonyDeduction"
            data={data}
            setData={setData}
            label={t('contract.alimony-deduction')}
          />
        </div>
        <LabelHeader label={t('contract.options-act-19-08-2023')} />
        <div>
          <CheckboxWithLabelField
            field="skipSanitarityInvoiceGeneration"
            data={data}
            setData={setData}
            label={t('contract.skip-sanitary-costs')}
          />

          <CheckboxWithLabelField
            field="skipAccommodationInvoiceGeneration"
            data={data}
            setData={setData}
            label={t('contract.skip-accommodation-costs')}
          />
        </div>
      </FormLayout>
    </div>
  )
}

export default ContractParameters
